import classNames from 'classnames';
import { hasRichTextContent } from '../../utils/richText';
import { RawHtml } from '../RawHtml/RawHtml';
import { BannerAioModule } from './sections/BannerAioModule';
import { CtaAioModule } from './sections/CtaAioModule';
import { FaqAioModule } from './sections/FaqAioModule';
import { ProductCardAioModule } from './sections/ProductCardAioModule/ProductCardAioModule';
import { RelatedReadingAioModule } from './sections/RelatedReadingAioModule/RelatedReadingAioModule';
import { AioModuleProps } from './types';

/**
 * AIO Module — M101's sponsored summary block inside blog articles (CMS-1937).
 *
 * Header plus five optional sections, each of which decides for itself whether
 * it has enough content to render, so an unused section leaves no trace.
 *
 * Two constraints the sections must keep honouring:
 *
 * - **Headings.** The module title and the FAQ section name are `<h3>`, both
 *   pinned by the ticket: SEO requirements 1-2 name them explicitly, and the
 *   "SEO heading structure" acceptance criterion asserts them. Do not
 *   "normalise" the FAQ name to `<h4>` for a tidier outline — that breaks an
 *   AC. The products heading has no such requirement (the ticket has no
 *   products-title field at all; it comes from the design), so it is an `<h4>`
 *   as a sub-section of the module. No `<h1>`/`<h2>` anywhere inside — those
 *   belong to the article. FAQ questions are accordion buttons, never heading
 *   tags.
 * - **Everything server-rendered.** All content, *including collapsed FAQ
 *   answers*, must be in the HTML on load — never mounted on click. The
 *   shared `CollapsibleSection` already behaves that way: it always renders
 *   `content` and hides it with `invisible max-h-0 overflow-hidden`. So
 *   composing that primitive satisfies the requirement; do not replace it
 *   with a conditional render.
 */
export const AioModule = ({
  title,
  paragraph,
  productsTitle,
  products,
  sharedProductAttribute,
  bannerImage,
  bannerUrl,
  faqName,
  faqItems,
  relevantArticles,
  ctaDescription,
  ctaLabel,
  ctaUrl,
  ...tracking
}: AioModuleProps): JSX.Element | null => {
  // `visible` is handled upstream: BlockMapper drops any block whose
  // `visible === false` before it reaches a component, so an expired
  // placement never gets here. The field stays on the type because that
  // filter reads it off the mapped component.

  // Title + paragraph are the module. CMS validation enforces both, so an
  // entry missing them is either legacy or a partial draft - render nothing
  // rather than an empty sponsored block.
  if (!title?.trim() || !hasRichTextContent(paragraph)) {
    return null;
  }

  // Each optional section renders only when the editor filled it in. A banner
  // needs both halves to be useful: an image with no link is not clickable,
  // and a link with no image has nothing to click. A product card with no
  // linked product has nothing to render at all, so it is dropped.
  const productCards = products.flatMap((card) =>
    card.product ? [{ ...card, product: card.product }] : [],
  );

  // The CTA row owns the card's bottom edge: its divider runs the full width,
  // so it sits outside the padded body and the closing border is drawn around
  // it. With no CTA that wrapper is gone and the body has to close the card
  // itself, hence the two rounding/border variants below.
  const ctaLabelText = ctaLabel?.trim();
  const ctaHref = ctaUrl?.trim();
  const hasCta = Boolean(ctaLabelText && ctaHref);

  return (
    <>
      <section
        data-component="aio-module"
        className="flex flex-col gap-spacing/gap/y"
      >
        <h3 className="mhg-alpha-title-1 text-text/accent">{title}</h3>

        <div
          className={classNames(
            'flex flex-col gap-spacing/gap/y rounded-t-radius/card border-x-2 border-t-2 border-buttons/primary bg-fill/card px-spacing/padding/x py-spacing/padding/y',
            { 'rounded-b-radius/card border-b-2': !hasCta },
          )}
        >
          {/* No emptiness check: the early return above already handled it. */}
          <RawHtml
            baseTextStyle="none"
            className="mhg-alpha-typography-body-1-blog text-text/primary"
            content={paragraph}
          />

          {productCards.length > 0 && (
            <section
              data-component="aio-module-products"
              className="flex flex-col gap-spacing/gap/soft-12px"
            >
              {/* The heading names the picks for this article ("Top Picks Cards
                  for Dining"), so it only makes sense with cards under it. */}
              {productsTitle?.trim() ? (
                <h4 className="mhg-alpha-bullet-2 text-text/secondary-lighter">
                  {productsTitle.trim()}
                </h4>
              ) : null}

              <div
                className={classNames('grid grid-cols-1 gap-spacing/gap/x', {
                  // Two cards share the row from md up; below that they stack.
                  // The schema caps products at 2, so no wider case exists.
                  'md:grid-cols-2': productCards.length > 1,
                })}
              >
                {productCards.map((card, index) => (
                  <ProductCardAioModule
                    key={card.id}
                    product={card.product}
                    highlightTag={card.highlightTag}
                    sharedProductAttribute={sharedProductAttribute}
                    index={index}
                    {...tracking}
                  />
                ))}
              </div>
            </section>
          )}

          {bannerImage?.url && bannerUrl?.trim() && (
            <BannerAioModule
              image={bannerImage}
              url={bannerUrl.trim()}
              showTopDivider={productCards.length > 0}
            />
          )}

          {/* Gates itself on having at least one complete question. These
              questions also feed the page-level FAQPage JSON-LD built by
              `faqSchemaUtils` in fe-services, so the module does not emit a
              graph of its own — one FAQPage per URL. */}
          <FaqAioModule faqName={faqName} items={faqItems} {...tracking} />

          <RelatedReadingAioModule links={relevantArticles} {...tracking} />
        </div>
      </section>

      {ctaLabelText && ctaHref && (
        <div className="rounded-b-radius/card border-x-2 border-b-2 border-buttons/primary bg-fill/card">
          <CtaAioModule
            description={ctaDescription}
            label={ctaLabelText}
            url={ctaHref}
            {...tracking}
          />
        </div>
      )}
    </>
  );
};
